import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { ADMIN_MENU_CONFIG, MENU_CONFIG } from "src/constants/menuConfig";
import { useAppSelector } from "src/redux/hooks";

interface MenuProps {
  type: "admin" | "tenant";
}

const Menu: React.FC<MenuProps> = ({ type = "tenant" }) => {
  const [showSubMenuIdx, setShowSubMenuIdx] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const menus = type === "admin" ? ADMIN_MENU_CONFIG : MENU_CONFIG;
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  const normalizePath = (path?: string) => path?.replace(/\/+$/, "") || "";
  const isPathActive = (path?: string) => {
    const normalized = normalizePath(path);
    const current = normalizePath(location.pathname);
    return (
      !!normalized &&
      (current === normalized || current.startsWith(`${normalized}/`))
    );
  };

  const menuList = useMemo(() => {
    const rawList = Object.keys(menus).map((menuKey) => menus[menuKey]);
    if (type === "admin" || isTenantAdmin) return rawList;

    const permissionEntries = Object.keys(permissions || {});
    const shouldFilter = permissionEntries.length > 0;
    if (!shouldFilter) return rawList;

    const hasPermission = (module?: string, action?: string) => {
      if (!module) return true;
      const modulePerms = permissions?.[module] || [];
      if (!action) return modulePerms.length > 0;
      return modulePerms.includes(action);
    };

    return rawList
      .map((menu: any) => {
        if (menu?.adminOnly) {
          return null;
        }
        if (!menu?.module) return menu;
        if (menu?.subMenu?.length) {
          const filteredSub = menu.subMenu.filter((subMenu: any) =>
            hasPermission(menu.module, subMenu.permission || "view")
          );
          if (filteredSub.length === 0) return null;
          return { ...menu, subMenu: filteredSub };
        }
        if (!hasPermission(menu.module, menu.permission || "view")) {
          return null;
        }
        return menu;
      })
      .filter(Boolean);
  }, [menus, permissions, isTenantAdmin, type]);

  const activeMenuIndex = useMemo(
    () =>
      menuList.findIndex((menu: any) => {
        if (menu?.subMenu?.length > 0) {
          const basePath =
            menu.path || menu.subMenu[0].path.split("/").slice(0, -1).join("/");
          return isPathActive(basePath);
        }
        return isPathActive(menu.path);
      }),
    [menuList, location.pathname]
  );

  useEffect(() => {
    const activeMenu = menuList[activeMenuIndex];
    if (
      activeMenu?.subMenu?.length > 0 &&
      activeMenuIndex !== -1 &&
      showSubMenuIdx === -1
    ) {
      setShowSubMenuIdx(activeMenuIndex);
    }
  }, [activeMenuIndex, menuList]);

  console.log(activeMenuIndex, showSubMenuIdx);

  return (
    <nav className="h-[calc(100vh-77px)] overflow-y-scroll">
      {menuList.map((menu: any, index: number) => {
        const Icon = menu.icon;
        const basePath =
          menu.path ||
          (menu?.subMenu?.[0]?.path
            ? menu.subMenu[0].path.split("/").slice(0, -1).join("/")
            : "");
        const isMenuActive = isPathActive(basePath);
        return (
          <div
            key={`menu-${index}`}
            className={`text-sm m-2 pr-2 rounded-md ${
              isMenuActive || showSubMenuIdx === index
                ? "bg-primary-100 text-primary-700 font-semibold"
                : "hover:bg-primary-50 hover:text-primary-600"
            }`}
          >
            <button
              className="w-full px-4 py-3 pr-2"
              onClick={() => {
                if (menu?.subMenu?.length > 0) {
                  setShowSubMenuIdx((prev) => (prev === index ? -1 : index));
                } else {
                  navigate(menu.path);
                }
              }}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Icon className="inline" size={16} />
                  {menu.label}
                </span>

                {menu?.subMenu?.length > 0 &&
                  (showSubMenuIdx === index ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  ))}
              </div>
            </button>
            {showSubMenuIdx === index && (
              <div className="pl-4 pb-2 flex flex-col gap-2">
                {menu?.subMenu?.map((subMenu: any, subMenuIdx: number) => {
                  const isSubMenuActive = isPathActive(subMenu.path);
                  return (
                    <button
                      key={`submenu-${index}-${subMenuIdx}`}
                      className={`w-full px-4 py-3 rounded-md flex items-center ${
                        isSubMenuActive
                          ? "bg-white text-primary-700 font-semibold"
                          : "hover:bg-white font-medium hover:font-semibold text-primary-700 hover:text-primary-600"
                      }`}
                      onClick={() => navigate(subMenu.path)}
                    >
                      {subMenu.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default Menu;
